import { useEffect, useState, useMemo } from "react";
import { Trash2, Download, Gift, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";

export default function AdminAnalyses() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ type: "", regio: "", payment_status: "", analysis_status: "" });
  const [selected, setSelected] = useState(new Set());
  const [busyRetry, setBusyRetry] = useState(null);

  const load = async () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    params.set("limit", "100");
    const { data } = await api.get(`/admin/analyses?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
    setSelected(new Set());
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [filters]);

  const allChecked = items.length > 0 && selected.size === items.length;
  const someChecked = selected.size > 0 && selected.size < items.length;

  const toggleAll = () => {
    if (allChecked) setSelected(new Set());
    else setSelected(new Set(items.map((r) => r.id)));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`${selected.size} analyse(s) permanent verwijderen (GDPR)?`)) return;
    const { data } = await api.post("/admin/analyses/bulk-delete", { ids: Array.from(selected) });
    toast.success(`${data.deleted} analyses verwijderd`);
    load();
  };

  const remove = async (id) => {
    if (!confirm("Analyse permanent verwijderen (GDPR)?")) return;
    await api.delete(`/admin/analyses/${id}`);
    toast.success("Analyse verwijderd");
    load();
  };

  const grantFreeRetry = async (id, email) => {
    const reason = prompt(
      `Gratis nieuwe scan sturen naar ${email}?\n\nDe klant krijgt 1 gratis credit + een mail met link om opnieuw te uploaden.\n\nKorte reden (optioneel, komt in admin-log):`,
      ""
    );
    if (reason === null) return; // cancelled
    setBusyRetry(id);
    try {
      const { data } = await api.post(`/admin/analyses/${id}/grant-free-retry`, { reason });
      if (data.email_sent) {
        toast.success(`Gratis scan verstuurd naar ${data.to}`);
      } else {
        toast.warning(`Credit toegekend, mail verzenden mislukt (zie logs). Klant: ${data.to}`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon gratis scan niet versturen");
    } finally {
      setBusyRetry(null);
    }
  };

  const exportCsv = () => {
    const rows = [["id","type","regio","amount_cents","score","payment_status","email","created"]];
    items.forEach((r) =>
      rows.push([
        r.id, r.offerte_type, r.regio, r.amount_cents, r.score ?? "",
        r.payment_status, r.customer?.email || "", r.created_at_iso,
      ])
    );
    const csv = rows.map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `analyses-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-7">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-bold text-oc-primary text-[34px]">Analyses</h1>
          <p className="text-oc-textSecondary mt-1">{total} resultaten</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 && (
            <Button
              data-testid="bulk-delete"
              onClick={bulkDelete}
              className="bg-oc-excessive hover:bg-oc-excessive/90 text-white rounded-oc-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" /> {selected.size} verwijderen
            </Button>
          )}
          <Button onClick={exportCsv} data-testid="export-csv" variant="outline" className="rounded-oc-btn border-oc-border text-oc-primary hover:bg-oc-subtle">
            <Download className="w-4 h-4 mr-2" /> Exporteer CSV
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-4 gap-3">
        <Input
          data-testid="filter-type"
          placeholder="Type bevat..."
          value={filters.type}
          onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          className="rounded-oc-btn"
        />
        <Input
          placeholder="Regio bevat..."
          value={filters.regio}
          onChange={(e) => setFilters({ ...filters, regio: e.target.value })}
          className="rounded-oc-btn"
        />
        <Select value={filters.payment_status || "all"} onValueChange={(v) => setFilters({ ...filters, payment_status: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-oc-btn" data-testid="filter-payment-status">
            <SelectValue placeholder="Betaling" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle betalingen</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.analysis_status || "all"} onValueChange={(v) => setFilters({ ...filters, analysis_status: v === "all" ? "" : v })}>
          <SelectTrigger className="rounded-oc-btn" data-testid="filter-analysis-status">
            <SelectValue placeholder="Analyse" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle analyses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="running">Bezig</SelectItem>
            <SelectItem value="completed">Voltooid</SelectItem>
            <SelectItem value="failed">Mislukt</SelectItem>
            <SelectItem value="bundle">Bundle</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-oc border border-oc-border overflow-hidden">
        <div className="grid grid-cols-12 px-6 py-3 bg-oc-subtle text-[12px] uppercase tracking-[0.05em] font-semibold text-oc-textSecondary items-center gap-2">
          <div className="col-span-1 flex items-center">
            <Checkbox
              data-testid="bulk-select-all"
              checked={allChecked}
              ref={(el) => { if (el) el.indeterminate = someChecked; }}
              onCheckedChange={toggleAll}
            />
          </div>
          <span className="col-span-3">Klant / Type</span>
          <span className="col-span-2">Regio</span>
          <span className="col-span-1">Bedrag</span>
          <span className="col-span-1">Score</span>
          <span className="col-span-2">Status</span>
          <span className="col-span-2 text-right">Acties</span>
        </div>
        <div className="divide-y divide-oc-border">
          {items.map((r) => {
            const failed = r.analysis_status === "failed";
            return (
            <div
              key={r.id}
              data-testid={`row-${r.id}`}
              className={`grid grid-cols-12 px-6 py-4 items-center text-[14px] gap-2 ${
                failed ? "bg-oc-excessive/5 hover:bg-oc-excessive/10" : "hover:bg-oc-subtle/40"
              }`}
            >
              <div className="col-span-1">
                <Checkbox
                  data-testid={`select-${r.id}`}
                  checked={selected.has(r.id)}
                  onCheckedChange={() => toggleOne(r.id)}
                />
              </div>
              <div className="col-span-3 min-w-0">
                <p className="font-display font-semibold text-oc-primary truncate flex items-center gap-2">
                  {failed && <AlertTriangle className="w-4 h-4 text-oc-excessive shrink-0" data-testid="failed-icon" />}
                  {r.customer?.first_name} {r.customer?.last_name}
                </p>
                <p className="text-oc-textSecondary text-[12px] truncate">{r.offerte_type}</p>
                {failed && r.analysis_error && (
                  <p className="text-oc-excessive text-[11px] truncate mt-0.5" title={r.analysis_error}>
                    Fout: {r.analysis_error}
                  </p>
                )}
              </div>
              <span className="col-span-2 text-oc-textSecondary text-[13px] truncate">{r.regio}</span>
              <span className="col-span-1 font-mono text-[13px]">€ {(r.amount_cents/100).toFixed(2)}</span>
              <span className="col-span-1 font-mono">{r.score ?? "—"}</span>
              <span className="col-span-2 flex flex-col gap-1">
                <span className={`text-[10px] uppercase font-semibold tracking-wide px-2 py-0.5 rounded-md inline-block w-fit ${
                  r.payment_status === "completed" ? "oc-status-fair" : r.payment_status === "failed" ? "oc-status-excessive" : "bg-oc-subtle text-oc-textSecondary"
                }`}>betaling: {r.payment_status}</span>
                <span
                  data-testid={`analysis-status-${r.id}`}
                  className={`text-[10px] uppercase font-semibold tracking-wide px-2 py-0.5 rounded-md inline-block w-fit ${
                    r.analysis_status === "completed"
                      ? "oc-status-fair"
                      : r.analysis_status === "failed"
                      ? "oc-status-excessive"
                      : r.analysis_status === "running"
                      ? "bg-oc-secondary/10 text-oc-secondary"
                      : "bg-oc-subtle text-oc-textSecondary"
                  }`}
                >
                  analyse: {r.analysis_status === "failed" ? "MISLUKT" : r.analysis_status}
                </span>
                {r.free_retry_granted && (
                  <span className="text-[10px] uppercase font-semibold tracking-wide px-2 py-0.5 rounded-md inline-block w-fit bg-oc-high/10 text-oc-high">
                    Gratis scan ✓
                  </span>
                )}
              </span>
              <span className="col-span-2 text-right flex justify-end gap-1">
                {failed && r.customer?.email && (
                  <Button
                    onClick={() => grantFreeRetry(r.id, r.customer.email)}
                    disabled={busyRetry === r.id}
                    variant="ghost"
                    size="sm"
                    data-testid={`grant-free-retry-${r.id}`}
                    title="Stuur gratis nieuwe scan naar klant"
                    className="text-oc-high hover:text-oc-high hover:bg-oc-high/10"
                  >
                    <Gift className="w-4 h-4" />
                  </Button>
                )}
                <Button onClick={() => remove(r.id)} variant="ghost" size="sm" className="text-oc-excessive hover:text-oc-excessive hover:bg-oc-excessive/10">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </span>
            </div>
          );
          })}
          {items.length === 0 && <p className="px-7 py-10 text-center text-oc-textSecondary">Geen resultaten</p>}
        </div>
      </div>
    </div>
  );
}
